import React, { useState } from 'react'
import { Modal } from 'semantic-ui-react'
import styled from 'styled-components'
import { AppPrimaryButton, AppSecondaryButton, AppDangerButton } from 'elements'
import { SubscriptionService } from 'utility/services/subscription'

const Body = styled.div`
    .title {
        font-size: 17px;
        font-weight: 700;
        margin: 0 0 6px;
    }
    .sub {
        font-size: 13px;
        color: #6b7280;
        margin: 0 0 12px;
        line-height: 1.5;
    }
    .offer {
        background: #f5f4ff;
        border: 1px solid #d5d1ff;
        border-radius: 8px;
        padding: 14px;
        text-align: center;
        margin: 12px 0;
    }
    .warn {
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        padding: 10px 12px;
        color: #991b1b;
        font-size: 13px;
        margin: 12px 0;
    }
    .note {
        background: #f8fafc;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 10px 12px;
        color: #374151;
        font-size: 13px;
        line-height: 1.5;
        margin: 0 0 12px;
    }
    .err {
        margin-top: 10px;
        color: #991b1b;
        font-size: 13px;
    }
    .actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-top: 12px;
    }
`

/**
 * GET User/Offer:
 *   New offer: { OfferId: 1, HasOffer: true, DiscountPercent, Message }
 *   Already used: { OfferId: 0, HasOffer: false, Reason: "AlreadyAccepted",
 *     AcceptedOffer: { Accepted: true, RetentionOfferId, Message, ValidUntil } }
 * OfferId 0 is not a real offer. Do not POST it.
 */
const formatOfferDate = value => {
    if (!value) return ''
    const raw = String(value).replace(/(\.\d+)/, '')
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })
}

const parseOffer = data => {
    const empty = {
        showOffer: false,
        alreadyAccepted: false,
        alreadyUsed: false,
        pct: 0,
        message: '',
        offerId: null,
        paidUntil: '',
    }
    if (!data || typeof data !== 'object') return empty
    const accepted = data.AcceptedOffer || data.acceptedOffer || null
    const reason = String(data.Reason || data.reason || '')
    const alreadyAccepted =
        reason === 'AlreadyAccepted' ||
        accepted?.Accepted === true ||
        accepted?.AlreadyAccepted === true
    const alreadyUsed = reason === 'AlreadyUsed'
    const rawId = Number(data.OfferId ?? data.offerId)
    const nestedId = Number(
        accepted?.RetentionOfferId ?? accepted?.OfferId ?? accepted?.offerId,
    )
    let offerId = null
    if (rawId > 0) {
        offerId = rawId
    } else if (!alreadyAccepted && nestedId > 0) {
        offerId = nestedId
    }
    const message =
        (alreadyAccepted && (accepted?.Message || accepted?.message)) ||
        data.Message ||
        data.message ||
        data.OfferText ||
        data.offerText ||
        ''
    const fromField = Number(
        data.DiscountPercent ??
            data.discountPercent ??
            accepted?.DiscountPercent ??
            0,
    )
    const fromMessage = Number((String(message).match(/(\d+)\s*%/) || [])[1])
    const pct = fromField > 0 ? fromField : fromMessage || 0
    const showOffer = !alreadyAccepted && !alreadyUsed && offerId != null
    const paidUntil = formatOfferDate(
        data.PaidUntil || data.paidUntil || accepted?.ValidUntil || accepted?.validUntil,
    )
    return {
        showOffer,
        alreadyAccepted,
        alreadyUsed,
        pct: pct || (showOffer ? 10 : 0),
        message,
        offerId,
        paidUntil,
    }
}

/**
 * Cancel flow: GET /User/Offer → confirm → POST /User/CancelPlan.
 */
export const CancelPlanModal = ({ open, onClose, onCancelled }) => {
    const [step, setStep] = useState('loading') // loading | offer | confirm | done
    const [offer, setOffer] = useState(null)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)

    React.useEffect(() => {
        if (!open) return undefined
        let cancelled = false
        setStep('loading')
        setError('')
        setOffer(null)
        const loadOffer = async () => {
            try {
                const data = await SubscriptionService.getRetentionOffer()
                if (cancelled) return
                const parsed = parseOffer(data)
                setOffer(data)
                if (parsed.alreadyAccepted) setStep('kept')
                else if (parsed.showOffer) setStep('offer')
                else setStep('confirm')
            } catch {
                if (cancelled) return
                // Mockup: Offer 500 / fail → skip retention, go to confirm
                setStep('confirm')
            }
        }
        loadOffer()
        return () => {
            cancelled = true
        }
    }, [open])

    const handleAccept = async () => {
        const { offerId } = parseOffer(offer)
        if (offerId == null) {
            setError('This offer is not available.')
            return
        }
        setBusy(true)
        setError('')
        try {
            await SubscriptionService.acceptOffer(offerId)
            setStep('kept')
        } catch (err) {
            const apiErr = err?.error
            setError(
                apiErr?.Errors?.[0]?.ErrorMessage ||
                    apiErr?.ErrorMessage ||
                    apiErr?.errorMessage ||
                    apiErr?.Message ||
                    apiErr?.message ||
                    err?.message ||
                    'Could not apply this offer. Please try again.',
            )
        } finally {
            setBusy(false)
        }
    }

    const handleCancel = async () => {
        setBusy(true)
        setError('')
        try {
            const data = await SubscriptionService.cancelPlan()
            setStep('done')
            onCancelled?.(data)
        } catch (err) {
            const apiErr = err?.error
            setError(
                apiErr?.Errors?.[0]?.ErrorMessage ||
                    apiErr?.message ||
                    apiErr?.Message ||
                    err?.message ||
                    'Could not cancel plan. Please try again.',
            )
        } finally {
            setBusy(false)
        }
    }

    const parsed = parseOffer(offer)
    const {
        pct,
        message: offerMessage,
        alreadyAccepted,
        alreadyUsed,
        paidUntil,
    } = parsed
    let accessLine =
        'Premium stays active until the end of the current billing period. After that date the account moves to the Free plan.'
    if (paidUntil) {
        accessLine = `Premium stays active until ${paidUntil}. After that date the account moves to the Free plan.`
    }
    const offerText =
        offerMessage ||
        (pct > 0 ? `Stay with us and save ${pct}%!` : 'Your plan stays active.')

    return (
        <Modal open={open} size="tiny" onClose={busy ? undefined : onClose}>
            <Modal.Content>
                <Body>
                    {step === 'loading' && (
                        <p className="sub">Checking offers…</p>
                    )}
                    {step === 'offer' && (
                        <>
                            <div className="title">
                                Wait — save {pct}% first?
                            </div>
                            <p className="sub">
                                You&apos;ll lose unlimited seats + templates.
                            </p>
                            <div className="offer">
                                <div style={{ fontSize: 28 }}>🎁</div>
                                <div style={{ fontWeight: 700 }}>
                                    Stay for {pct}% off the next 3 months
                                </div>
                                <p className="sub">{offerText}</p>
                            </div>
                            <div className="actions">
                                <AppPrimaryButton
                                    type="button"
                                    loading={busy}
                                    onClick={handleAccept}
                                >
                                    Keep Monthly with discount
                                </AppPrimaryButton>
                                {error && <div className="err">{error}</div>}
                                <AppSecondaryButton
                                    type="button"
                                    onClick={() => setStep('confirm')}
                                    disabled={busy}
                                >
                                    No thanks, cancel anyway
                                </AppSecondaryButton>
                            </div>
                        </>
                    )}
                    {step === 'confirm' && (
                        <>
                            <div className="title">Cancel subscription</div>
                            {alreadyUsed && (
                                <div className="note">
                                    The 10% retention offer has already been
                                    used on this account, so it is not available
                                    again.
                                </div>
                            )}
                            <p className="sub">
                                {accessLine} You can purchase Premium again at
                                any time.
                            </p>
                            <div className="note">
                                Cancelling stops the next renewal. Nothing
                                changes until you confirm below.
                            </div>
                            {error && <div className="err">{error}</div>}
                            <div className="actions">
                                <AppDangerButton
                                    type="button"
                                    loading={busy}
                                    onClick={handleCancel}
                                >
                                    Yes, cancel my plan
                                </AppDangerButton>
                                <AppSecondaryButton
                                    type="button"
                                    onClick={onClose}
                                    disabled={busy}
                                >
                                    Keep my plan
                                </AppSecondaryButton>
                            </div>
                        </>
                    )}
                    {step === 'kept' && (
                        <>
                            <div className="title">
                                {alreadyAccepted
                                    ? 'Discount already applied'
                                    : 'Discount applied'}
                            </div>
                            <p className="sub">{offerText}</p>
                            <div className="actions">
                                <AppPrimaryButton type="button" onClick={onClose}>
                                    Back to Account
                                </AppPrimaryButton>
                                <AppSecondaryButton
                                    type="button"
                                    onClick={() => setStep('confirm')}
                                >
                                    No thanks, cancel anyway
                                </AppSecondaryButton>
                            </div>
                        </>
                    )}
                    {step === 'done' && (
                        <>
                            <div className="title">Plan cancelled</div>
                            <p className="sub">
                                Renewal has stopped. Premium stays active until
                                the date on Account &amp; Billing, then the
                                account moves to the Free plan.
                            </p>
                            <AppPrimaryButton type="button" onClick={onClose}>
                                Back to Account
                            </AppPrimaryButton>
                        </>
                    )}
                </Body>
            </Modal.Content>
        </Modal>
    )
}
